import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { FaPlus, FaEdit, FaTrash, FaClock, FaCalendarAlt } from 'react-icons/fa';
import api from '../services/api';
import { normalizeListForAdmin } from '../utils/normalizeApi';

function TimetableManagement() {
  const [timetables, setTimetables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [filterClass, setFilterClass] = useState('All');
  const [filterDay, setFilterDay] = useState('All');
  
  const [formData, setFormData] = useState({
    className: '',
    section: 'A',
    dayOfWeek: 'Monday',
    periodNumber: 1,
    subject: '',
    teacherName: '',
    roomNumber: '',
    startTime: '',
    endTime: ''
  });

  const classOptions = ['Pre-Nursery', 'Nursery', 'KG', 'Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 
                        'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10', 'Class 11', 'Class 12'];
  
  const sectionOptions = ['A', 'B', 'C', 'D'];
  const dayOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const periodOptions = [1, 2, 3, 4, 5, 6, 7, 8];

  useEffect(() => { fetchTimetables(); }, []);

  const fetchTimetables = async () => {
    try {
      const response = await api.get('/timetable');
      console.log('📅 Fetched timetables:', response.data);
      setTimetables(normalizeListForAdmin(response.data));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching timetables:', error);
      setTimetables([
        { id: 1, className: 'Class 10', section: 'A', dayOfWeek: 'Monday', periodNumber: 1, subject: 'Mathematics', teacherName: 'Mr. Sharma', roomNumber: 'Room 101', startTime: '08:00', endTime: '08:45' }
      ]);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/timetable/${editingId}`, formData);
        alert('✅ Timetable entry updated successfully!');
      } else {
        await api.post('/timetable', formData);
        alert('✅ Timetable entry added successfully!');
      }
      setShowForm(false);
      setEditingId(null);
      resetForm();
      fetchTimetables();
    } catch (error) {
      console.error('Timetable CRUD Error:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
      alert('❌ Error: ' + errorMsg);
    }
  };

  const handleEdit = (item) => {
    console.log('📝 Editing timetable:', item);
    setEditingId(item.id);
    setFormData({
      className: item.className || '',
      section: item.section || 'A',
      dayOfWeek: item.dayOfWeek || 'Monday',
      periodNumber: item.periodNumber || 1,
      subject: item.subject || '',
      teacherName: item.teacherName || '',
      roomNumber: item.roomNumber || '',
      startTime: item.startTime || '',
      endTime: item.endTime || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this timetable entry?')) return;
    try {
      await api.delete(`/timetable/${id}`);
      alert('✅ Timetable entry deleted successfully!');
      fetchTimetables();
    } catch (error) {
      console.error('Delete error:', error);
      alert('❌ Error deleting timetable entry');
    }
  };

  const resetForm = () => {
    setFormData({ className: '', section: 'A', dayOfWeek: 'Monday', periodNumber: 1, subject: '', teacherName: '', roomNumber: '', startTime: '', endTime: '' });
  };

  const filteredTimetables = timetables.filter(t => 
    (filterClass === 'All' || t.className === filterClass) &&
    (filterDay === 'All' || t.dayOfWeek === filterDay)
  );

  if (loading) return <AdminLayout><div className="p-6">Loading...</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="p-6">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-blue-900">Timetable Management</h1>
            <p className="text-gray-600 mt-1">Manage class schedules and periods</p>
          </div>
          <button onClick={() => setShowForm(true)}
            className="bg-blue-900 text-white px-6 py-3 rounded-lg hover:bg-blue-800 transition flex items-center gap-2 shadow-lg">
            <FaPlus /> Add Period
          </button>
        </motion.div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-md p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-blue-900" />
              <label className="font-medium text-gray-700">Filter by Class:</label>
              <select value={filterClass} onChange={(e) => setFilterClass(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900">
                <option value="All">All Classes</option>
                {classOptions.map(cls => <option key={cls} value={cls}>{cls}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <FaClock className="text-blue-900" />
              <label className="font-medium text-gray-700">Filter by Day:</label>
              <select value={filterDay} onChange={(e) => setFilterDay(e.target.value)}
                className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-900">
                <option value="All">All Days</option>
                {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Timetable Table */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-900 to-blue-800 text-white">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">Class</th>
                  <th className="px-6 py-4 text-left font-semibold">Day</th>
                  <th className="px-6 py-4 text-left font-semibold">Period</th>
                  <th className="px-6 py-4 text-left font-semibold">Subject</th>
                  <th className="px-6 py-4 text-left font-semibold">Teacher</th>
                  <th className="px-6 py-4 text-left font-semibold">Room</th>
                  <th className="px-6 py-4 text-left font-semibold">Time</th>
                  <th className="px-6 py-4 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredTimetables.map((item, index) => (
                  <tr key={item.id} className={`hover:bg-blue-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}`}>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-blue-900">{item.className}</span>
                      <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">{item.section}</span>
                    </td>
                    <td className="px-6 py-4 text-gray-700">{item.dayOfWeek}</td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                        Period {item.periodNumber}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-semibold text-gray-800">{item.subject}</td>
                    <td className="px-6 py-4 text-gray-600">{item.teacherName}</td>
                    <td className="px-6 py-4 text-gray-600">{item.roomNumber}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <FaClock className="text-blue-900" />
                        <span>{item.startTime} - {item.endTime}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center gap-2">
                        <button onClick={() => handleEdit(item)} className="text-blue-900 hover:text-blue-700 transition p-2"><FaEdit size={18} /></button>
                        <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800 transition p-2"><FaTrash size={18} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        {filteredTimetables.length === 0 && (
          <div className="text-center text-gray-600 mt-12 py-8">
            <p className="text-xl">No timetable entries found</p>
            <p className="text-sm mt-2">Click "Add Period" to create one</p>
          </div>
        )}

        {/* Modal Form */}
        {showForm && (
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">{editingId ? 'Edit' : 'Add'} Timetable Entry</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Class *</label>
                    <select value={formData.className} onChange={(e) => setFormData({ ...formData, className: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      <option value="">Select Class</option>
                      {classOptions.map(cls => <option key={cls} value={cls}>{cls}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Section *</label>
                    <select value={formData.section} onChange={(e) => setFormData({ ...formData, section: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      {sectionOptions.map(sec => <option key={sec} value={sec}>{sec}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Day *</label>
                    <select value={formData.dayOfWeek} onChange={(e) => setFormData({ ...formData, dayOfWeek: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      {dayOptions.map(day => <option key={day} value={day}>{day}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Period Number *</label>
                    <select value={formData.periodNumber} onChange={(e) => setFormData({ ...formData, periodNumber: parseInt(e.target.value) })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900">
                      {periodOptions.map(p => <option key={p} value={p}>Period {p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <input type="text" value={formData.subject} onChange={(e) => setFormData({ ...formData, subject: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Mathematics" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Teacher Name *</label>
                    <input type="text" value={formData.teacherName} onChange={(e) => setFormData({ ...formData, teacherName: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Mr. Sharma" />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Room Number</label>
                    <input type="text" value={formData.roomNumber} onChange={(e) => setFormData({ ...formData, roomNumber: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" placeholder="e.g., Room 101" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Time *</label>
                    <input type="time" value={formData.startTime} onChange={(e) => setFormData({ ...formData, startTime: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Time *</label>
                    <input type="time" value={formData.endTime} onChange={(e) => setFormData({ ...formData, endTime: e.target.value })} required
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-900" />
                  </div>
                </div>
                <div className="flex gap-4 pt-4">
                  <button type="submit" className="flex-1 bg-blue-900 text-white py-3 rounded-lg hover:bg-blue-800 transition font-semibold">
                    {editingId ? 'Update' : 'Add'} Period
                  </button>
                  <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition font-semibold">Cancel</button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </div>
    </AdminLayout>
  );
}

export default TimetableManagement;
